// 📧 ZOHO MAIL EMAIL SERVICE
// This service handles sending emails via Zoho Mail API

class ZohoEmailService {
  constructor() {
    // Zoho API configuration - SET THESE VALUES
    this.config = {
      clientId: process.env.REACT_APP_ZOHO_CLIENT_ID || 'YOUR_CLIENT_ID',
      clientSecret: process.env.REACT_APP_ZOHO_CLIENT_SECRET || 'YOUR_CLIENT_SECRET',
      refreshToken: process.env.REACT_APP_ZOHO_REFRESH_TOKEN || 'YOUR_REFRESH_TOKEN',
      fromEmail: process.env.REACT_APP_ZOHO_FROM_EMAIL || 'admin@app.acctoo.com',
      apiBaseUrl: 'https://mail.zoho.com/api'
    };
    
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  // Get fresh access token using refresh token
  async getAccessToken() {
    try {
      // Check if current token is still valid
      if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
        return this.accessToken;
      }

      console.log('🔄 Refreshing Zoho access token...');

      const response = await fetch('https://accounts.zoho.com/oauth/v2/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: this.config.clientId,
          client_secret: this.config.clientSecret,
          refresh_token: this.config.refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(`Token refresh error: ${data.error_description || data.error}`);
      }

      this.accessToken = data.access_token;
      this.tokenExpiry = Date.now() + (data.expires_in * 1000) - 60000; // 1 minute buffer
      
      console.log('✅ Zoho access token refreshed successfully');
      return this.accessToken;

    } catch (error) {
      console.error('❌ Error refreshing Zoho access token:', error);
      throw error;
    }
  }

  // Send email via Zoho Mail API
  async sendEmail({ to, subject, htmlContent, textContent }) {
    try {
      console.log('📧 Sending email via Zoho Mail API...', { to, subject });

      const accessToken = await this.getAccessToken();

      const emailData = {
        fromAddress: this.config.fromEmail,
        toAddress: to,
        subject: subject,
        content: htmlContent || textContent,
        contentType: htmlContent ? 'html' : 'text'
      };

      const response = await fetch(`${this.config.apiBaseUrl}/accounts/${this.config.fromEmail}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Email send failed: ${response.status} ${response.statusText} - ${errorData}`);
      }

      const result = await response.json();
      console.log('✅ Email sent successfully via Zoho:', result);
      
      return { success: true, messageId: result.messageId || result.data?.messageId };

    } catch (error) {
      console.error('❌ Error sending email via Zoho:', error);
      return { success: false, error: error.message };
    }
  }

  // Send employee invitation email
  async sendEmployeeInvite({ 
    employeeName, 
    employeeEmail, 
    companyName, 
    designation, 
    department, 
    inviteLink, 
    customMessage = '' 
  }) {
    try {
      const subject = `Invitation to join ${companyName} on ACCTOO`;
      
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #2563eb; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; }
        .invite-button { 
          display: inline-block; 
          background: #2563eb; 
          color: white; 
          padding: 12px 24px; 
          text-decoration: none; 
          border-radius: 6px; 
          margin: 20px 0; 
          font-weight: bold;
        }
        .footer { background: #374151; color: #9ca3af; padding: 20px; text-align: center; border-radius: 0 0 8px 8px; font-size: 14px; }
        .details { background: white; padding: 15px; border-radius: 6px; margin: 15px 0; border-left: 4px solid #2563eb; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🎉 You're Invited to Join ${companyName}!</h1>
        </div>
        
        <div class="content">
            <h2>Dear ${employeeName},</h2>
            
            <p>Congratulations! You have been invited to join <strong>${companyName}</strong> on the ACCTOO platform.</p>
            
            <div class="details">
                <h3>📋 Your Role Details:</h3>
                <p><strong>Position:</strong> ${designation}</p>
                <p><strong>Department:</strong> ${department}</p>
                <p><strong>Company:</strong> ${companyName}</p>
            </div>
            
            ${customMessage ? `
            <div class="details">
                <h3>💬 Message from your employer:</h3>
                <p><em>"${customMessage}"</em></p>
            </div>
            ` : ''}
            
            <p>To accept this invitation and set up your account, please click the button below:</p>
            
            <div style="text-align: center;">
                <a href="${inviteLink}" class="invite-button">Accept Invitation & Join Now</a>
            </div>
            
            <p style="font-size: 14px; color: #6b7280;">
                <strong>⏰ Important:</strong> This invitation will expire in 7 days. 
                If the button doesn't work, you can copy and paste this link into your browser:<br>
                <code style="background: #f3f4f6; padding: 4px 8px; border-radius: 4px; word-break: break-all;">${inviteLink}</code>
            </p>
            
            <div class="details">
                <h3>🚀 What's Next?</h3>
                <ol>
                    <li>Click the invitation link above</li>
                    <li>Create your ACCTOO account (or sign in if you already have one)</li>
                    <li>Complete your profile setup</li>
                    <li>Start collaborating with your team!</li>
                </ol>
            </div>
            
            <p>If you have any questions, please contact your employer or reach out to our support team.</p>
            
            <p>Welcome to the team!</p>
        </div>
        
        <div class="footer">
            <p><strong>ACCTOO</strong> - Simplifying Accounting, Together</p>
            <p>🌐 <a href="https://app.app.acctoo.com" style="color: #60a5fa;">app.acctoo.com</a> | 📧 support@app.acctoo.com</p>
            <p style="font-size: 12px; margin-top: 10px;">
                This email was sent to ${employeeEmail} because you were invited to join ${companyName} on ACCTOO.
            </p>
        </div>
    </div>
</body>
</html>`;

      const textContent = `Dear ${employeeName},

You have been invited to join ${companyName} as ${designation} in the ${department} department.

${customMessage}

Please click the link below to accept the invitation:
${inviteLink}

This invitation will expire in 7 days.

Best regards,
${companyName} Team

--
ACCTOO - Simplifying Accounting, Together
https://app.app.acctoo.com`;

      return await this.sendEmail({
        to: employeeEmail,
        subject,
        htmlContent,
        textContent
      });

    } catch (error) {
      console.error('❌ Error sending employee invite:', error);
      return { success: false, error: error.message };
    }
  }

  // Send email verification
  async sendEmailVerification({ userEmail, userName, verificationLink }) {
    try {
      const subject = 'Verify your email address - ACCTOO';
      
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #16a34a; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f0fdf4; padding: 30px; border: 1px solid #bbf7d0; }
        .verify-button { 
          display: inline-block; 
          background: #16a34a; 
          color: white; 
          padding: 12px 24px; 
          text-decoration: none; 
          border-radius: 6px; 
          margin: 20px 0; 
          font-weight: bold;
        }
        .footer { background: #374151; color: #9ca3af; padding: 20px; text-align: center; border-radius: 0 0 8px 8px; font-size: 14px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🔐 Verify Your Email Address</h1>
        </div>
        
        <div class="content">
            <h2>Hello ${userName || 'there'}!</h2>
            
            <p>Welcome to ACCTOO! To complete your account setup and ensure the security of your account, please verify your email address.</p>
            
            <div style="text-align: center;">
                <a href="${verificationLink}" class="verify-button">Verify Email Address</a>
            </div>
            
            <p style="font-size: 14px; color: #059669;">
                <strong>🔒 Why verify?</strong> Email verification helps us:
            </p>
            <ul style="font-size: 14px; color: #059669;">
                <li>Protect your account from unauthorized access</li>
                <li>Send you important notifications</li>
                <li>Help with account recovery if needed</li>
            </ul>
            
            <p style="font-size: 14px; color: #6b7280;">
                If the button doesn't work, copy and paste this link into your browser:<br>
                <code style="background: #f3f4f6; padding: 4px 8px; border-radius: 4px; word-break: break-all;">${verificationLink}</code>
            </p>
            
            <p style="font-size: 14px; color: #dc2626;">
                <strong>⚠️ Didn't create an account?</strong> If you didn't sign up for ACCTOO, please ignore this email.
            </p>
        </div>
        
        <div class="footer">
            <p><strong>ACCTOO</strong> - Simplifying Accounting, Together</p>
            <p>🌐 <a href="https://app.app.acctoo.com" style="color: #60a5fa;">app.acctoo.com</a> | 📧 support@app.acctoo.com</p>
        </div>
    </div>
</body>
</html>`;

      const textContent = `Hello ${userName || 'there'}!

Welcome to ACCTOO! Please verify your email address by clicking the link below:

${verificationLink}

Why verify? Email verification helps us protect your account and send important notifications.

If you didn't create an account, please ignore this email.

--
ACCTOO - Simplifying Accounting, Together
https://app.app.acctoo.com`;

      return await this.sendEmail({
        to: userEmail,
        subject,
        htmlContent,
        textContent
      });

    } catch (error) {
      console.error('❌ Error sending email verification:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const emailService = new ZohoEmailService();
export default emailService;
